import { Button } from "@hub/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@hub/components/ui/table";
import type { OrgSwitches } from "@hub/lib/org-types";
import { availableTo, BOTS, FEATURES, INTEGRATIONS, type RegistryItem } from "@hub/lib/registry";

export function toggleId(list: string[], id: string): string[] {
  return (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]).sort();
}

/** Settings may only name what is entitled; dropping an entitlement drops the setting. */
export function narrowSettings(entitlements: OrgSwitches, settings: OrgSwitches): OrgSwitches {
  return {
    features: settings.features.filter((id) => entitlements.features.includes(id)),
    integrations: settings.integrations.filter((id) => entitlements.integrations.includes(id)),
    bots: settings.bots.filter((id) => entitlements.bots.includes(id)),
  };
}

const GROUPS: { key: keyof OrgSwitches; label: string; items: readonly RegistryItem[] }[] = [
  { key: "features", label: "Features", items: FEATURES },
  { key: "integrations", label: "Integrations", items: INTEGRATIONS },
  { key: "bots", label: "Bots", items: BOTS },
];

function Switch({
  on,
  disabled,
  label,
  onToggle,
}: {
  on: boolean;
  disabled?: boolean;
  label: string;
  onToggle: () => void;
}) {
  return (
    <Button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      variant={on ? "default" : "outline"}
      size="sm"
      disabled={disabled}
      onClick={onToggle}
    >
      {on ? "On" : "Off"}
    </Button>
  );
}

/**
 * Two columns per id: "Allowed" (entitlements, what Nymbl permits) and "On"
 * (settings, within entitlements). Spec §6 keeps them separate so a customer
 * admin can later own the second column without a migration.
 */
export function OrgSwitchesForm({
  entitlements,
  settings,
  group,
  slug,
  onChange,
}: {
  entitlements: OrgSwitches;
  group: keyof OrgSwitches;
  settings: OrgSwitches;
  slug: string | null;
  onChange: (next: { entitlements: OrgSwitches; settings: OrgSwitches }) => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      {group === "bots" ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Add a bot from the Helm catalog to make it available for this organization. Select Save
            changes to apply. People can then add available bots in their own Helm.
          </p>
          {BOTS.map((bot) => {
            const available = slug !== null && availableTo(bot, slug);
            const enabled = entitlements.bots.includes(bot.id) && settings.bots.includes(bot.id);
            return (
              <div
                key={bot.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
              >
                <div className="min-w-0">
                  <h3 className="font-medium">{bot.label}</h3>
                  <p className="text-sm text-muted-foreground">{bot.description}</p>
                  {available ? (
                    <p className="text-xs text-muted-foreground">
                      {enabled ? "Available to this organization" : "Not added"}
                    </p>
                  ) : null}
                  {!available ? (
                    <p className="text-xs text-muted-foreground">
                      Not available for this organization.
                    </p>
                  ) : null}
                </div>
                <Button
                  type="button"
                  aria-label={`${enabled ? "Remove" : "Add"} ${bot.label}`}
                  variant={enabled ? "secondary" : "outline"}
                  disabled={!available}
                  onClick={() =>
                    onChange({
                      entitlements: {
                        ...entitlements,
                        bots: enabled
                          ? entitlements.bots.filter((id) => id !== bot.id)
                          : [...new Set([...entitlements.bots, bot.id])].sort(),
                      },
                      settings: {
                        ...settings,
                        bots: enabled
                          ? settings.bots.filter((id) => id !== bot.id)
                          : [...new Set([...settings.bots, bot.id])].sort(),
                      },
                    })
                  }
                >
                  {enabled ? "Remove bot" : "Add bot"}
                </Button>
              </div>
            );
          })}
        </div>
      ) : null}
      {GROUPS.filter((g) => g.key === group).map((g) => (
        <details key={g.key} open={g.key !== "bots" ? true : undefined}>
          <summary className="min-h-11 text-sm font-semibold">
            {g.key === "bots" ? "Advanced bot access" : `${g.label} access`}
          </summary>
          <p className="mb-3 text-sm text-muted-foreground">
            Allowed controls what this organization may use. On controls what is enabled now.
            Turning off Allowed also turns off On.
          </p>
          {g.key === "integrations" && slug !== "nymbl" ? (
            <p className="mb-2 text-xs text-muted-foreground">
              Slack and GitHub are not available to organizations other than Nymbl in this release;
              switching them on here changes nothing on the boxes.
            </p>
          ) : null}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Allowed</TableHead>
                <TableHead>On</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {g.items.map((item) => {
                const { id } = item;
                const allowed = entitlements[g.key].includes(id);
                const on = settings[g.key].includes(id);
                return (
                  <TableRow key={id}>
                    <TableCell>
                      <span className="font-medium">{item.label}</span>
                      <span className="mt-1 block text-xs text-muted-foreground">
                        {item.description}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Switch
                        on={allowed}
                        label={`${id} allowed`}
                        onToggle={() => {
                          const nextEnt = {
                            ...entitlements,
                            [g.key]: toggleId(entitlements[g.key], id),
                          };
                          onChange({
                            entitlements: nextEnt,
                            settings: narrowSettings(nextEnt, settings),
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        on={on}
                        disabled={!allowed}
                        label={`${id} on`}
                        onToggle={() =>
                          onChange({
                            entitlements,
                            settings: { ...settings, [g.key]: toggleId(settings[g.key], id) },
                          })
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </details>
      ))}
    </div>
  );
}
